import { useParams, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { celebrityPosts } from '../../mocks/celebritySaju';
import CelebrityDreamSection from './components/CelebrityDreamSection';

const elementColors: Record<string, string> = {
  wood: 'text-emerald-400',
  fire: 'text-red-400',
  earth: 'text-amber-500',
  metal: 'text-slate-300',
  water: 'text-sky-400',
};

const elementLabels: Record<string, string> = {
  wood: '목(木)',
  fire: '화(火)',
  earth: '토(土)',
  metal: '금(金)',
  water: '수(水)',
};

const elementBg: Record<string, string> = {
  wood: 'bg-emerald-500/20 border-emerald-500/30',
  fire: 'bg-red-500/20 border-red-500/30',
  earth: 'bg-amber-500/20 border-amber-500/30',
  metal: 'bg-slate-500/20 border-slate-500/30',
  water: 'bg-sky-500/20 border-sky-500/30',
};

export default function CelebrityDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = celebrityPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-cosmic-gradient flex items-center justify-center">
        <Navbar />
        <div className="text-center">
          <p className="text-purple-300 text-lg mb-4">해당 인물을 찾을 수 없습니다.</p>
          <Link to="/celebrity-saju" className="text-primary-gold underline">목록으로 돌아가기</Link>
        </div>
      </div>
    );
  }

  const pillarsArray = [
    { label: '년주(年柱)', ...post.fourPillars.year },
    { label: '월주(月柱)', ...post.fourPillars.month },
    { label: '일주(日柱)', ...post.fourPillars.day },
    ...(post.fourPillars.hour ? [{ label: '시주(時柱)', ...post.fourPillars.hour }] : []),
  ];

  const elementEntries = Object.entries(post.fiveElements) as [keyof typeof post.fiveElements, number][];
  const maxElement = Math.max(...elementEntries.map(([, v]) => v));

  const relatedPosts = celebrityPosts.filter((p) => p.id !== post.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-cosmic-gradient">
      <Navbar />

      {/* Hero */}
      <div className="relative pt-20">
        <div className="relative w-full h-80 lg:h-96 overflow-hidden">
          <img
            src={post.thumbnail}
            alt={post.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0720] via-[#0d0720]/60 to-[#0d0720]/30"></div>

          {/* Back button */}
          <Link
            to="/celebrity-saju"
            className="absolute top-6 left-6 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-sm border border-white/20 text-white text-xs hover:border-primary-gold/50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-sm"></i>
            </div>
            <span>목록으로</span>
          </Link>
        </div>

        {/* Title card overlapping hero */}
        <div className="relative max-w-5xl mx-auto px-4 -mt-32">
          <div className="p-6 lg:p-8 rounded-2xl bg-gradient-to-br from-[#1a0f2e]/95 to-[#0f0a1e]/98 border border-white/10 backdrop-blur-xl">
            <div className="flex flex-col lg:flex-row lg:items-start gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <span className="px-3 py-1 rounded-full bg-primary-gold/20 border border-primary-gold/40 text-primary-gold text-xs font-bold tracking-wider">
                    {post.category}
                  </span>
                  <span className="text-purple-400 text-xs">{post.publishedAt} 분석 · {post.readTime}분 읽기</span>
                </div>

                <h1 className="text-3xl lg:text-4xl font-black text-white mb-1"
                  style={{ fontFamily: '"Noto Serif KR", serif' }}>
                  {post.name}
                </h1>
                <p className="text-purple-400 mb-4">{post.nameEn} · {post.birthDate} · {post.birthPlace}</p>
                <p className="text-purple-200 leading-relaxed">{post.summary}</p>
              </div>

              {/* Key info badges */}
              <div className="grid grid-cols-3 gap-3 lg:w-64">
                <div className="col-span-3 p-3 rounded-xl bg-primary-gold/10 border border-primary-gold/20 text-center">
                  <p className="text-primary-gold text-xs mb-1">일간(日干)</p>
                  <p className="text-white font-bold">{post.dayMaster}</p>
                </div>
                <div className="p-2.5 rounded-lg bg-white/5 border border-white/8 text-center">
                  <p className="text-purple-400 text-[10px] mb-1">태양궁</p>
                  <p className="text-white text-xs font-semibold leading-tight">{post.astrology.sun.split(' ')[0]}</p>
                </div>
                <div className="p-2.5 rounded-lg bg-white/5 border border-white/8 text-center">
                  <p className="text-purple-400 text-[10px] mb-1">달자리</p>
                  <p className="text-white text-xs font-semibold leading-tight">{post.astrology.moon.split(' ')[0]}</p>
                </div>
                <div className="p-2.5 rounded-lg bg-white/5 border border-white/8 text-center">
                  <p className="text-purple-400 text-[10px] mb-1">주성</p>
                  <p className="text-white text-xs font-semibold leading-tight">{post.ziwei.mainStar.replace('(', '').replace(')', '').split('星')[0]}성</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left main content */}
          <div className="lg:col-span-2 space-y-8">

            {/* Four Pillars Table */}
            <section className="p-6 rounded-2xl bg-gradient-to-br from-[#1a0f2e]/80 to-[#0f0a1e]/90 border border-white/10">
              <h2 className="text-white text-xl font-bold mb-6 flex items-center gap-3"
                style={{ fontFamily: '"Noto Serif KR", serif' }}>
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-500/20 border border-amber-500/30 text-amber-400 text-sm font-bold">四</span>
                사주팔자 (四柱八字)
              </h2>

              {/* Pillars grid */}
              <div className="grid grid-cols-4 gap-3 mb-6">
                {pillarsArray.map((pillar) => (
                  <div key={pillar.label} className="text-center">
                    <p className="text-purple-500 text-xs mb-2">{pillar.label}</p>
                    <div className={`py-3 rounded-t-xl border-t border-x font-bold text-lg ${
                      pillar.label === '일주(日柱)'
                        ? 'bg-primary-gold/20 border-primary-gold/40 text-primary-gold'
                        : 'bg-white/5 border-white/10 text-white'
                    }`}
                      style={{ fontFamily: '"Noto Serif KR", serif' }}>
                      {pillar.heavenly.charAt(0)}
                    </div>
                    <div className="py-3 rounded-b-xl border-b border-x bg-white/3 border-white/8 text-purple-300 font-semibold"
                      style={{ fontFamily: '"Noto Serif KR", serif' }}>
                      {pillar.earthly.charAt(0)}
                    </div>
                    <p className="text-purple-500 text-[10px] mt-2">{pillar.element}</p>
                    <p className="text-purple-400/60 text-[10px]">{pillar.animal}띠</p>
                  </div>
                ))}
              </div>

              <div className="p-4 rounded-xl bg-white/3 border border-white/8">
                <p className="text-purple-200 text-sm leading-relaxed">{post.sajuAnalysis}</p>
              </div>
            </section>

            {/* Five Elements */}
            <section className="p-6 rounded-2xl bg-gradient-to-br from-[#1a0f2e]/80 to-[#0f0a1e]/90 border border-white/10">
              <h2 className="text-white text-xl font-bold mb-6 flex items-center gap-3"
                style={{ fontFamily: '"Noto Serif KR", serif' }}>
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-500/20 border border-teal-500/30 text-teal-400 text-sm font-bold">五</span>
                오행(五行) 분포
              </h2>
              <div className="space-y-3">
                {elementEntries.map(([key, value]) => (
                  <div key={key} className="flex items-center gap-4">
                    <div className={`w-16 flex-shrink-0 px-2 py-1 rounded-lg border text-center text-xs font-bold ${elementBg[key]} ${elementColors[key]}`}>
                      {elementLabels[key]}
                    </div>
                    <div className="flex-1 h-3 rounded-full bg-white/5 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-1000 ${
                          key === 'wood' ? 'bg-emerald-400' :
                          key === 'fire' ? 'bg-red-400' :
                          key === 'earth' ? 'bg-amber-500' :
                          key === 'metal' ? 'bg-slate-400' : 'bg-sky-400'
                        } ${value === maxElement ? 'opacity-100' : 'opacity-60'}`}
                        style={{ width: `${(value / (maxElement + 1)) * 100}%` }}
                      ></div>
                    </div>
                    <div className="flex gap-1">
                      {Array.from({ length: value }, (_, i) => (
                        <div key={i} className={`w-2 h-2 rounded-full ${elementColors[key].replace('text-', 'bg-')}`}></div>
                      ))}
                      {value === 0 && <div className="w-2 h-2 rounded-full bg-white/10"></div>}
                    </div>
                    <span className={`text-sm font-bold w-4 text-center ${value === maxElement ? elementColors[key] : 'text-purple-500'}`}>{value}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Western Astrology */}
            <section className="p-6 rounded-2xl bg-gradient-to-br from-[#1a0f2e]/80 to-[#0f0a1e]/90 border border-white/10">
              <h2 className="text-white text-xl font-bold mb-6 flex items-center gap-3"
                style={{ fontFamily: '"Noto Serif KR", serif' }}>
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-purple-500/20 border border-purple-500/30 text-purple-400 text-sm font-bold">★</span>
                서양 점성술 분석
              </h2>
              <div className="grid grid-cols-3 gap-4 mb-5">
                <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 text-center">
                  <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                    <i className="ri-sun-line text-amber-400 text-xl"></i>
                  </div>
                  <p className="text-amber-400/70 text-xs mb-1">태양궁</p>
                  <p className="text-white text-sm font-bold">{post.astrology.sun}</p>
                </div>
                <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/20 text-center">
                  <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                    <i className="ri-moon-line text-purple-300 text-xl"></i>
                  </div>
                  <p className="text-purple-400/70 text-xs mb-1">달자리</p>
                  <p className="text-white text-sm font-bold">{post.astrology.moon}</p>
                </div>
                <div className="p-4 rounded-xl bg-teal-500/10 border border-teal-500/20 text-center">
                  <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                    <i className="ri-arrow-up-circle-line text-teal-300 text-xl"></i>
                  </div>
                  <p className="text-teal-400/70 text-xs mb-1">상승궁</p>
                  <p className="text-white text-sm font-bold">{post.astrology.rising}</p>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-white/3 border border-white/8">
                <p className="text-purple-200 text-sm leading-relaxed">{post.astrology.description}</p>
              </div>
            </section>

            {/* Ziwei Doushu */}
            <section className="p-6 rounded-2xl bg-gradient-to-br from-[#1a0f2e]/80 to-[#0f0a1e]/90 border border-white/10">
              <h2 className="text-white text-xl font-bold mb-6 flex items-center gap-3"
                style={{ fontFamily: '"Noto Serif KR", serif' }}>
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-rose-500/20 border border-rose-500/30 text-rose-400 text-sm font-bold">紫</span>
                자미두수 (紫微斗數) 분석
              </h2>
              <div className="flex flex-col sm:flex-row gap-4 mb-5">
                <div className="flex-1 p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-center">
                  <p className="text-rose-400/70 text-xs mb-1">명궁 주성</p>
                  <p className="text-white font-bold">{post.ziwei.mainStar}</p>
                </div>
                <div className="flex-1 p-4 rounded-xl bg-purple-500/10 border border-purple-500/20 text-center">
                  <p className="text-purple-400/70 text-xs mb-1">궁위</p>
                  <p className="text-white font-bold text-sm">{post.ziwei.palace}</p>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-white/3 border border-white/8">
                <p className="text-purple-200 text-sm leading-relaxed">{post.ziwei.description}</p>
              </div>
            </section>

            {/* Comprehensive Analysis */}
            <section className="p-6 rounded-2xl bg-gradient-to-br from-primary-gold/5 to-amber-900/10 border border-primary-gold/20">
              <h2 className="text-primary-gold text-xl font-bold mb-4 flex items-center gap-3"
                style={{ fontFamily: '"Noto Serif KR", serif' }}>
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-gold/20 border border-primary-gold/30 text-primary-gold text-sm font-bold">綜</span>
                종합 운명 풀이
              </h2>
              <p className="text-purple-100 text-sm leading-loose">{post.comprehensiveAnalysis}</p>
            </section>

            {/* Key Strengths */}
            <section className="p-6 rounded-2xl bg-gradient-to-br from-[#1a0f2e]/80 to-[#0f0a1e]/90 border border-white/10">
              <h2 className="text-white text-xl font-bold mb-5"
                style={{ fontFamily: '"Noto Serif KR", serif' }}>
                타고난 핵심 강점
              </h2>
              <div className="space-y-3">
                {post.keyStrengths.map((strength, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-gold/20 border border-primary-gold/30">
                      <span className="text-primary-gold text-xs font-bold">{i + 1}</span>
                    </div>
                    <p className="text-purple-200 text-sm leading-relaxed">{strength}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Life Advice */}
            <section className="p-6 rounded-2xl bg-gradient-to-br from-teal-900/20 to-[#0f0a1e]/80 border border-teal-500/20">
              <h2 className="text-teal-300 text-xl font-bold mb-4 flex items-center gap-2"
                style={{ fontFamily: '"Noto Serif KR", serif' }}>
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-lightbulb-line text-teal-400"></i>
                </div>
                운명을 완성하는 조언
              </h2>
              <p className="text-purple-200 text-sm leading-relaxed">{post.lifeAdvice}</p>
            </section>

            {/* Celebrity Dream Section */}
            <CelebrityDreamSection
              slug={post.slug}
              name={post.name}
              dayMaster={post.dayMaster}
            />
          </div>

          {/* Right sidebar */}
          <aside className="space-y-6">
            {/* CTA */}
            <div className="p-5 rounded-2xl bg-gradient-to-br from-primary-gold/10 to-amber-900/10 border border-primary-gold/20">
              <p className="text-primary-gold font-bold text-sm mb-2">내 사주도 분석해보세요</p>
              <p className="text-purple-300 text-xs leading-relaxed mb-4">
                AI가 사주팔자, 점성술, 자미두수를 3종 통합 분석해드립니다.
              </p>
              <Link
                to="/"
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-primary-gold text-black text-sm font-bold hover:bg-amber-400 transition-colors whitespace-nowrap"
              >
                <span>분석 시작하기</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </Link>
            </div>

            {/* Summary card */}
            <div className="p-5 rounded-2xl bg-gradient-to-br from-[#1a0f2e]/80 to-[#0f0a1e]/90 border border-white/10">
              <h3 className="text-white font-bold mb-4 text-sm"
                style={{ fontFamily: '"Noto Serif KR", serif' }}>
                분석 요약
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-purple-400 text-xs">이름</span>
                  <span className="text-white text-xs font-semibold">{post.name}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-purple-400 text-xs">생년월일</span>
                  <span className="text-white text-xs font-semibold">{post.birthDate}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-purple-400 text-xs">출생지</span>
                  <span className="text-white text-xs font-semibold">{post.birthPlace}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-purple-400 text-xs">일간</span>
                  <span className="text-primary-gold text-xs font-bold">{post.dayMaster}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-purple-400 text-xs">태양궁</span>
                  <span className="text-white text-xs font-semibold">{post.astrology.sun}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-purple-400 text-xs">자미 주성</span>
                  <span className="text-rose-300 text-xs font-semibold">{post.ziwei.mainStar}</span>
                </div>
                <div className="flex justify-between items-start py-2">
                  <span className="text-purple-400 text-xs">태그</span>
                  <div className="flex flex-wrap gap-1 justify-end max-w-32">
                    {post.tags.map((tag) => (
                      <span key={tag} className="text-[10px] text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded-full">#{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Related posts */}
            <div className="p-5 rounded-2xl bg-gradient-to-br from-[#1a0f2e]/80 to-[#0f0a1e]/90 border border-white/10">
              <h3 className="text-white font-bold mb-4 text-sm"
                style={{ fontFamily: '"Noto Serif KR", serif' }}>
                다른 인물 분석
              </h3>
              <div className="space-y-3">
                {relatedPosts.map((related) => (
                  <Link
                    key={related.id}
                    to={`/celebrity-saju/${related.slug}`}
                    className="flex items-center gap-3 group cursor-pointer"
                  >
                    <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                      <img src={related.thumbnail} alt={related.name} className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold group-hover:text-primary-gold transition-colors">{related.name}</p>
                      <p className="text-purple-500 text-xs">{related.dayMaster} · {related.category}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
}
